/**
 * Pooled particle system — pure canvas, zero allocation per frame after warmup.
 * All coordinates are WORLD space (the renderer's world transform is active).
 * Capacity is fixed; when full, the oldest slot is recycled so a watermelon
 * barrage can never grow the pool.
 */

export type ParticleKind =
  | 'spark' // bright short-lived streak from impacts
  | 'smoke' // slow grey puff, grows and rises
  | 'debris' // rotating dirt/rock chip with gravity
  | 'dust' // soft ground poof (landings, footsteps)
  | 'splat' // juicy weapon-coloured blob
  | 'leaf' // drifting leaf (props, deaths)
  | 'splash' // water droplet
  | 'ring'; // expanding shockwave outline

interface Particle {
  alive: boolean;
  kind: ParticleKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number; // seconds remaining
  maxLife: number;
  size: number; // start radius (px)
  grow: number; // radius change per second (smoke/ring)
  grav: number; // px/s² (positive = down)
  drag: number; // velocity damping per second (0 = none)
  rot: number;
  spin: number;
  color: string;
  alpha: number; // peak opacity
}

export interface BurstOpts {
  color?: string;
  count?: number;
  /** Base speed, px/s. */
  speed?: number;
  /** Extra random speed added on top of `speed`. */
  jitter?: number;
  size?: number;
  /** Life in seconds. */
  life?: number;
  /** Direction in radians; omit for omnidirectional. */
  angle?: number;
  /** Half-cone width in radians (only with `angle`). */
  spread?: number;
  gravity?: number;
}

const rand = (a: number, b: number): number => a + Math.random() * (b - a);

export class Particles {
  private readonly pool: Particle[];
  private cursor = 0;

  constructor(private readonly capacity = 600) {
    this.pool = Array.from({ length: capacity }, () => ({
      alive: false,
      kind: 'spark' as ParticleKind,
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      life: 0,
      maxLife: 1,
      size: 2,
      grow: 0,
      grav: 0,
      drag: 0,
      rot: 0,
      spin: 0,
      color: '#fff',
      alpha: 1,
    }));
  }

  get activeCount(): number {
    let n = 0;
    for (const p of this.pool) if (p.alive) n++;
    return n;
  }

  /** Drop everything (match restart). */
  clear(): void {
    for (const p of this.pool) p.alive = false;
  }

  private take(): Particle {
    // Prefer a dead slot; otherwise steal the one the cursor last handed out.
    for (let i = 0; i < this.capacity; i++) {
      const p = this.pool[(this.cursor + i) % this.capacity];
      if (!p.alive) {
        this.cursor = (this.cursor + i + 1) % this.capacity;
        return p;
      }
    }
    const p = this.pool[this.cursor];
    this.cursor = (this.cursor + 1) % this.capacity;
    return p;
  }

  /** Generic burst. Defaults tuned per kind when opts omit values. */
  burst(kind: ParticleKind, x: number, y: number, opts: BurstOpts = {}): void {
    const count = opts.count ?? 8;
    for (let i = 0; i < count; i++) {
      const p = this.take();
      const dir =
        opts.angle === undefined
          ? rand(0, Math.PI * 2)
          : opts.angle + (opts.spread === undefined ? rand(-Math.PI, Math.PI) : rand(-opts.spread, opts.spread));
      const spd = (opts.speed ?? 80) + rand(0, opts.jitter ?? 60);
      p.alive = true;
      p.kind = kind;
      p.x = x;
      p.y = y;
      p.vx = Math.cos(dir) * spd;
      p.vy = Math.sin(dir) * spd;
      p.maxLife = p.life = (opts.life ?? 0.7) * rand(0.6, 1.15);
      p.size = (opts.size ?? 3) * rand(0.7, 1.3);
      p.grav = opts.gravity ?? 0;
      p.drag = 0;
      p.rot = rand(0, Math.PI * 2);
      p.spin = rand(-8, 8);
      p.color = opts.color ?? '#fff';
      p.alpha = 1;
      this.applyKindDefaults(p, kind, opts);
    }
  }

  private applyKindDefaults(p: Particle, kind: ParticleKind, opts: BurstOpts): void {
    switch (kind) {
      case 'spark':
        p.grav = opts.gravity ?? 220;
        p.drag = 1.4;
        p.alpha = 1;
        break;
      case 'smoke':
        p.grav = opts.gravity ?? -28; // rises
        p.drag = 0.9;
        p.grow = 14;
        p.alpha = 0.5;
        p.vx *= 0.35;
        p.vy *= 0.35;
        break;
      case 'debris':
        p.grav = opts.gravity ?? 640;
        p.drag = 0.2;
        p.alpha = 1;
        break;
      case 'dust':
        p.grav = opts.gravity ?? -14;
        p.drag = 2.2;
        p.grow = 10;
        p.alpha = 0.55;
        break;
      case 'splat':
        p.grav = opts.gravity ?? 700;
        p.drag = 0.5;
        p.alpha = 0.95;
        break;
      case 'leaf':
        p.grav = opts.gravity ?? 90;
        p.drag = 1.1;
        p.alpha = 0.95;
        break;
      case 'splash':
        p.grav = opts.gravity ?? 520;
        p.drag = 0.4;
        p.alpha = 0.9;
        break;
      case 'ring': {
        // One particle acts as the whole ring: no motion; draw() eases the
        // radius from `size` outward toward `spin` (final radius).
        p.vx = 0;
        p.vy = 0;
        p.grav = 0;
        p.grow = 0;
        p.size = 6;
        p.alpha = 0.9;
        p.maxLife = p.life = (opts.life ?? 0.35) * rand(0.9, 1.1);
        p.spin = opts.size ?? 60;
        break;
      }
    }
  }

  /**
   * Standard explosion recipe: shockwave ring + weapon-coloured sparks +
   * dirt debris + a smoke cloud, all scaled by the blast radius.
   */
  explosion(x: number, y: number, r: number, color = '#e8c24a'): void {
    this.burst('ring', x, y, { size: r, life: 0.32 });
    this.burst('spark', x, y, {
      color,
      count: Math.round(Math.min(30, Math.max(12, r / 5))),
      speed: r * 1.6,
      jitter: r * 2.2,
      size: 2.6,
      life: 0.55,
    });
    this.burst('spark', x, y, {
      color: '#fff3c4',
      count: Math.round(Math.min(14, Math.max(6, r / 9))),
      speed: r * 1.1,
      jitter: r * 1.4,
      size: 2,
      life: 0.3,
    });
    this.burst('debris', x, y, {
      color: '#7a5a3a',
      count: Math.round(Math.min(22, Math.max(8, r / 7))),
      speed: r * 1.3,
      jitter: r * 1.6,
      size: 3.4,
      life: 1.1,
      angle: -Math.PI / 2, // thrown upward mostly
      spread: 1.9,
    });
    this.burst('smoke', x, y, {
      color: '#b9b3a8',
      count: Math.round(Math.min(16, Math.max(6, r / 11))),
      speed: r * 0.7,
      jitter: r * 0.7,
      size: Math.max(6, r * 0.22),
      life: 1.3,
    });
  }

  update(dt: number): void {
    const d = Math.min(dt, 0.05); // tab-blip clamp
    for (const p of this.pool) {
      if (!p.alive) continue;
      p.life -= d;
      if (p.life <= 0) {
        p.alive = false;
        continue;
      }
      p.vy += p.grav * d;
      if (p.drag > 0) {
        const k = Math.max(0, 1 - p.drag * d);
        p.vx *= k;
        p.vy *= k;
      }
      // Leaves flutter sideways as they fall.
      if (p.kind === 'leaf') p.vx += Math.sin((p.maxLife - p.life) * 7) * 34 * d;
      p.x += p.vx * d;
      p.y += p.vy * d;
      p.rot += p.spin * d;
      p.size = Math.max(0.5, p.size + p.grow * d);
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (const p of this.pool) {
      if (!p.alive) continue;
      const t = p.life / p.maxLife; // 1 → 0
      // Smoke/dust swell and dissolve; sparks pop hard then fade.
      const a = p.alpha * (p.kind === 'smoke' || p.kind === 'dust' ? t * t : Math.min(1, t * 1.6));
      if (a <= 0.01) continue;
      ctx.globalAlpha = a;
      switch (p.kind) {
        case 'ring': {
          // `spin` holds the final radius: ease from current size outward.
          const prog = 1 - t;
          const rad = p.size + (p.spin - p.size) * (1 - (1 - prog) * (1 - prog));
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 3 * t + 0.5;
          ctx.beginPath();
          ctx.arc(p.x, p.y, rad, 0, Math.PI * 2);
          ctx.stroke();
          break;
        }
        case 'debris':
        case 'leaf': {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.fillStyle = p.color;
          if (p.kind === 'leaf') {
            ctx.beginPath();
            ctx.ellipse(0, 0, p.size * 1.4, p.size * 0.6, 0, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
          }
          ctx.restore();
          break;
        }
        case 'smoke':
        case 'dust': {
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        default: {
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.globalAlpha = 1;
  }
}
